import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Users, Calendar, Heart, MapPin, Quote, Check, X, ShoppingBag, Star, Tag, ShieldCheck, Flag, Lock } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import Footer from '@/components/Footer';
import UserAvatar from '@/components/UserAvatar';
import SocialProofSection from '@/components/SocialProofSection';
import api from '@/lib/api';

const HERO_AVATARS = [
  { src: "https://images.unsplash.com/photo-1762522921456-cdfe882d36c3?auto=format&fit=crop&w=150&q=80", name: "Sarah" },
  { src: "https://images.unsplash.com/photo-1770058428154-9eee8a6a1fbb?auto=format&fit=crop&w=150&q=80", name: "Maya" },
  { src: "https://images.unsplash.com/photo-1762522928601-862bf2a04902?auto=format&fit=crop&w=150&q=80", name: "Jen" },
];

export default function LandingPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track QR code scan and store ref for signup
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      localStorage.setItem('cmc_ref', ref);
      api.post('/track/scan', { ref }).catch(() => {});
    }
  }, [searchParams]);

  const handleCTA = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      navigate('/signup');
    }
  };

  return (
    <div className="min-h-screen bg-warm-white font-body">

      {/* Navigation */}
      <nav 
        data-testid="main-navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'glass shadow-soft py-4' : 'bg-transparent py-6'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <span className="font-display text-2xl text-navy">
              Cali<span className="italic-teal">Moms</span> Connect
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              data-testid="nav-signin-btn"
              className="text-navy font-semibold text-sm px-5 py-2.5 rounded-full border border-navy/20 hover:border-teal hover:text-teal transition-colors"
            >
              Sign In
            </Link>
            <button
              onClick={handleCTA}
              data-testid="nav-join-btn"
              className="bg-teal text-white px-6 py-2.5 rounded-full font-semibold text-sm hover:bg-teal-deep transition-colors flex items-center gap-2 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
            >
              Join Now <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </nav>

      {/* ─── Hero Section ─── */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 px-6 md:px-12" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-navy leading-[1.1] tracking-tight">
                Motherhood, <span className="italic-teal">Elevated.</span>
              </h1>
              <p className="font-display text-2xl md:text-3xl text-navy leading-snug">
                Your village. Your city. <span className="italic-teal">Your people.</span>
              </p>
              <p className="text-text-mid text-lg md:text-xl leading-relaxed max-w-xl">
                Curated connections, members-only events, local discounts, and a marketplace built for moms who want more.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleCTA}
                  data-testid="hero-cta-btn"
                  className="bg-teal text-white px-8 py-4 rounded-full font-bold text-base hover:bg-teal-deep transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 flex items-center justify-center gap-2"
                >
                  Join Free <ArrowRight size={18} />
                </button>
                <a 
                  href="#how-it-works"
                  data-testid="see-how-link"
                  className="text-navy font-semibold underline decoration-2 underline-offset-4 decoration-seafoam hover:decoration-teal transition-colors px-4 py-4 text-center"
                >
                  See how it works
                </a>
              </div>
              <div className="flex items-center gap-4 pt-4">
                <div className="flex -space-x-3">
                  {HERO_AVATARS.map((a, i) => (
                    <UserAvatar
                      key={i}
                      src={a.src}
                      firstName={a.name}
                      size="w-10 h-10"
                      textSize="text-sm"
                      className="border-2 border-white"
                    />
                  ))}
                </div>
                <p className="text-text-mid text-sm">
                  Join free — upgrade anytime.
                </p>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-seafoam/30 rounded-[32px] p-8 md:p-12 relative">
                <div className="flex items-center justify-center">
                  <span className="font-display text-4xl md:text-5xl text-navy">
                    Cali<span className="italic-teal">Moms</span>
                  </span>
                </div>
                
                <motion.div 
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="absolute -bottom-4 -left-4 md:-left-8 bg-white rounded-2xl p-4 shadow-floating"
                >
                  <div className="flex items-center gap-3">
                    <MapPin className="text-teal" size={20} />
                    <span className="text-sm font-medium text-navy">Moms near you are waiting</span>
                  </div>
                </motion.div>

                <motion.div 
                  animate={{ y: [0, 8, 0] }}
                  transition={{ duration: 2.5, repeat: Infinity, delay: 0.5 }}
                  className="absolute -top-4 -right-4 md:-right-8 cursor-pointer"
                  onClick={handleCTA}
                  data-testid="hero-badge-cta"
                >
                  <div className="bg-teal text-white rounded-full px-5 py-2.5 shadow-lg hover:bg-teal-deep transition-colors">
                    <span className="text-sm font-bold">Free to Join</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── Social Proof (Stats + Testimonials) ─── */}
      <SocialProofSection />

      {/* ─── Community Section ─── */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-cream" data-testid="community-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                title: "Reimagining the village",
                text: "CMC blends thoughtful technology with real-world experiences to rebuild the community modern motherhood was always meant to have.",
                icon: Sparkles
              },
              {
                title: "Your village, reimagined",
                text: "We bridge the digital and the real — so whether you're connecting online or gathering in person, your community is always within reach.",
                icon: Heart
              },
              {
                title: "Small talk, no depth",
                text: "Plenty of friendly acquaintances. But who can you actually call when things get hard? Real friendships have become impossibly rare.",
                icon: Users
              },
              {
                title: "No time for the wrong fit",
                text: "You have maybe two free hours a week. The last thing you need is more forced socializing with people you have nothing in common with.",
                icon: Calendar
              }
            ].map((card, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="bg-white rounded-[32px] p-8 md:p-10 shadow-card border border-border-subtle hover:shadow-floating transition-all duration-500"
              >
                <card.icon className="text-teal mb-6" size={32} />
                <h3 className="font-display text-xl md:text-2xl text-navy mb-4">{card.title}</h3>
                <p className="text-text-mid leading-relaxed">{card.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── How It Works — 3 steps ─── */}
      <section id="how-it-works" className="py-24 md:py-32 px-6 md:px-12" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="font-display text-4xl md:text-5xl text-navy mb-4">
              How It <span className="italic-teal">Works</span>
            </h2>
            <p className="text-text-mid text-lg max-w-2xl mx-auto">
              Three steps to curate your village.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-10">
            {/* Step 1 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-[32px] p-8 md:p-10 shadow-card border border-border-subtle"
            >
              <div className="w-14 h-14 rounded-2xl bg-seafoam/40 flex items-center justify-center mb-6">
                <div className="relative">
                  <Heart className="text-teal" size={24} />
                  <MapPin className="text-teal absolute -bottom-1 -right-2" size={14} />
                </div>
              </div>
              <span className="text-xs uppercase tracking-widest font-bold text-teal">Step 01</span>
              <h3 className="font-display text-xl md:text-2xl text-navy mt-2 mb-4">Find Your People</h3>
              <p className="text-text-mid leading-relaxed">
                Not just any moms — <em>your</em> moms. Our AI-powered matching connects you with women who get it. Same season of motherhood, same vibe, same zip code. No awkward small talk. No endless scrolling. Just real, curated connections that actually stick.
              </p>
            </motion.div>

            {/* Step 2 — featured */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="bg-teal rounded-[32px] p-8 md:p-10 text-white shadow-xl"
            >
              <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mb-6">
                <div className="relative">
                  <Calendar className="text-white" size={24} />
                  <Star className="text-white absolute -top-1 -right-2" size={12} fill="currentColor" />
                </div>
              </div>
              <span className="text-xs uppercase tracking-widest font-bold text-white/70">Step 02</span>
              <h3 className="font-display text-xl md:text-2xl text-white mt-2 mb-4">Live Your Village</h3>
              <p className="text-white/90 leading-relaxed">
                This is where membership comes alive. Discover free and exclusive members-only events happening in your city — think intimate brunches, wellness workshops, and expert-led evenings designed for moms who want more. Plus unlock curated local business discounts from brands that actually align with your life.
              </p>
            </motion.div>

            {/* Step 3 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white rounded-[32px] p-8 md:p-10 shadow-card border border-border-subtle"
            >
              <div className="w-14 h-14 rounded-2xl bg-seafoam/40 flex items-center justify-center mb-6">
                <ShoppingBag className="text-teal" size={24} />
              </div>
              <span className="text-xs uppercase tracking-widest font-bold text-teal">Step 03</span>
              <h3 className="font-display text-xl md:text-2xl text-navy mt-2 mb-4">Buy, Sell & Discover</h3>
              <p className="text-text-mid leading-relaxed">
                Your new favorite place to find gently loved baby gear, connect with trusted nannies, discover local tutors, and sell what you no longer need — all within a community you actually trust. Think elevated marketplace meets neighborhood group, minus the spam.
              </p>
            </motion.div>
          </div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <button
              onClick={handleCTA}
              data-testid="how-it-works-cta"
              className="bg-teal text-white px-8 py-4 rounded-full font-bold text-base hover:bg-teal-deep transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 inline-flex items-center gap-2"
            >
              Get Started Free <ArrowRight size={18} />
            </button>
            <p className="text-text-light text-sm mt-3">Start building your village today</p>
          </motion.div>
        </div>
      </section>

      {/* ─── Features Strip ─── */}
      <section className="py-12 px-6 md:px-12 bg-seafoam/20" data-testid="features-strip">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-wrap justify-center gap-x-10 gap-y-6">
            {[
              { icon: Sparkles, label: "AI-Powered Matching" },
              { icon: Calendar, label: "Free & Exclusive Events" },
              { icon: Tag, label: "Local Business Discounts" },
              { icon: ShoppingBag, label: "Community Marketplace" },
              { icon: Users, label: "Curated Circles" },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <f.icon className="text-teal" size={18} />
                <span className="text-navy font-medium text-sm whitespace-nowrap">{f.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Trust Section ─── */}
      <section className="py-20 md:py-24 px-6 md:px-12" data-testid="trust-section">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-display text-3xl md:text-4xl text-navy">
              A community you can <span className="italic-teal">trust</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                Icon: ShieldCheck,
                title: 'Verified Members',
                body: 'Every member is reviewed to keep our community safe and genuine.',
                testId: 'trust-pillar-verified',
              },
              {
                Icon: Flag,
                title: 'Report & Block',
                body: 'See something off? Report or block any member instantly — our team reviews every report.',
                testId: 'trust-pillar-report',
              },
              {
                Icon: Lock,
                title: 'Private by Default',
                body: 'Your personal details are never shared without your permission.',
                testId: 'trust-pillar-private',
              },
            ].map((pillar, i) => (
              <motion.div
                key={pillar.testId}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-card text-center"
                data-testid={pillar.testId}
              >
                <div className="w-14 h-14 mx-auto rounded-full bg-teal/10 flex items-center justify-center mb-4">
                  <pillar.Icon className="text-teal" size={26} />
                </div>
                <h3 className="font-display text-xl text-navy mb-2">{pillar.title}</h3>
                <p className="text-text-mid text-sm leading-relaxed">{pillar.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Before / After ─── */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-cream" data-testid="before-after-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-[32px] p-8 md:p-12"
            >
              <h3 className="font-display text-2xl md:text-3xl text-text-light mb-8">Before</h3>
              <div className="space-y-4">
                {[
                  "Spending weekends alone, scrolling social media",
                  "Small talk that never goes anywhere",
                  "Driving home more alone than when you left",
                  "Wondering if it's just harder to make adult friends",
                  "Feeling isolated in the middle of a crowd"
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <X className="text-text-light mt-1 flex-shrink-0" size={18} />
                    <span className="text-text-mid">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-teal rounded-[32px] p-8 md:p-12 text-white"
            >
              <h3 className="font-display text-2xl md:text-3xl mb-8">After</h3>
              <div className="space-y-4">
                {[
                  "A warm group chat that lights up with real support",
                  "Sunday hikes and coffee dates that feel effortless",
                  "Friends who know your full story",
                  "Community that makes California feel like home",
                  "Knowing you are not alone"
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check className="text-white mt-1 flex-shrink-0" size={18} />
                    <span className="text-white/90">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── Final CTA ─── */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-navy" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-4xl md:text-5xl text-white mb-6 leading-tight">
              Your people are already out there.<br />
              <span className="italic-teal" style={{ color: '#C8DDD8' }}>Let's find them for you.</span>
            </h2>
            <p className="text-white/70 text-lg mb-10 max-w-2xl mx-auto">
              Join free today — no credit card needed. Upgrade anytime to unlock events, discounts, circles, and more.
            </p>
            <button
              onClick={handleCTA}
              data-testid="final-cta-btn"
              className="bg-white text-navy px-10 py-4 rounded-full font-bold text-lg hover:bg-cream transition-all shadow-xl hover:shadow-2xl hover:-translate-y-0.5 inline-flex items-center gap-2"
            >
              Join Free <ArrowRight size={20} />
            </button>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
